import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useLocation, useNavigate } from "react-router-dom";
import { getLeagueRegistrationsForCheckoff, updateRegistrationPaid } from "../../services/api/games";

export default function VenmoManualCheckoffView() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const leagueId = state?.leagueId;

  const [games, setGames] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [updating, setUpdating] = React.useState({});

  React.useEffect(() => {
    if (!leagueId) {
      setLoading(false);
      return;
    }
    getLeagueRegistrationsForCheckoff(leagueId)
      .then((data) => setGames(Array.isArray(data) ? data : []))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load registrations."))
      .finally(() => setLoading(false));
  }, [leagueId]);

  const handleTogglePaid = async (registrationId, currentValue) => {
    setUpdating((prev) => ({ ...prev, [registrationId]: true }));
    try {
      await updateRegistrationPaid(registrationId, !currentValue);
      setGames((prev) =>
        prev.map((game) => ({
          ...game,
          registrations: game.registrations.map((reg) =>
            reg.id === registrationId ? { ...reg, hasPaid: !currentValue } : reg
          ),
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update payment status.");
    } finally {
      setUpdating((prev) => ({ ...prev, [registrationId]: false }));
    }
  };

  if (!leagueId) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={2}>
          <Typography variant="h5">Venmo Check-off</Typography>
          <Alert severity="error">
            No league selected. Please navigate here from your league's Manage page.
          </Alert>
          <Button variant="outlined" onClick={() => navigate("/my-leagues")}>
            Back to My Leagues
          </Button>
        </Stack>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  const totalRegistrations = games.reduce((sum, g) => sum + g.registrations.length, 0);
  const totalPaid = games.reduce((sum, g) => sum + g.registrations.filter((r) => r.hasPaid).length, 0);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
        </Stack>

        <Box>
          <Typography variant="h4" component="h3" gutterBottom>
            Venmo Check-off
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip
              icon={<CheckCircleIcon />}
              label={`${totalPaid} / ${totalRegistrations} paid`}
              color={totalPaid === totalRegistrations && totalRegistrations > 0 ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {games.length === 0 ? (
          <Typography color="text.secondary">No games with registrations found for this league.</Typography>
        ) : (
          <Stack spacing={3}>
            {games.map((game) => {
              const d = new Date(game.startsAt);
              const dateStr = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
              const timeStr = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
              const paidCount = game.registrations.filter((r) => r.hasPaid).length;
              const totalCount = game.registrations.length;

              return (
                <Box key={game.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" component="h3">
                        {game.arenaName || "Game"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {dateStr} · {timeStr}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${paidCount} / ${totalCount} paid`}
                      size="small"
                      color={paidCount === totalCount && totalCount > 0 ? "success" : "default"}
                      variant="outlined"
                    />
                  </Stack>

                  {game.registrations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No registrations yet.</Typography>
                  ) : (
                    <Stack spacing={0}>
                      {game.registrations.map((reg, i) => (
                        <React.Fragment key={reg.id}>
                          {i > 0 && <Divider />}
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={0.5}
                            sx={{ py: 0.75 }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {reg.user.firstName} {reg.user.lastName}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {reg.user.email}
                                </Typography>
                                <Chip
                                  label={reg.role === "GOALIE" ? "Goalie" : "Skater"}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: "0.65rem" }}
                                />
                              </Stack>
                            </Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={reg.hasPaid}
                                  onChange={() => handleTogglePaid(reg.id, reg.hasPaid)}
                                  disabled={!!updating[reg.id]}
                                  color="success"
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2" color={reg.hasPaid ? "success.main" : "text.secondary"}>
                                  {updating[reg.id] ? "Saving…" : reg.hasPaid ? "Paid" : "Not paid"}
                                </Typography>
                              }
                              sx={{ mr: 0 }}
                            />
                          </Stack>
                        </React.Fragment>
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
